<template>
  <v-dialog v-model="showDialog" width="450" persistent>
    <v-card>
      <v-card-title class="flex items-center">
        <span>{{ t("categories.editInitialAmountTitle") }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          :aria-label="t('common.close')"
          variant="text"
          size="small"
          :disabled="saving"
          @click="showDialog = false"
        />
      </v-card-title>
      <v-card-text>
        <v-form id="edit-initial-amount-form" ref="form" :disabled="saving" @submit.prevent="save">
          <div class="text-sm text-medium-emphasis mb-4">
            {{ t("categories.editInitialAmountHelp") }}
          </div>
          <v-text-field
            v-model.number="initialAmount"
            :rules="[nonnegativeNumber]"
            type="number"
            min="0"
            step="0.01"
            autofocus
          >
            <template #label>{{ t("categories.initialAmount") }}</template>
          </v-text-field>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn :disabled="saving" @click="showDialog = false">{{ t("common.cancel") }}</v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          type="submit"
          form="edit-initial-amount-form"
          color="primary"
          :loading="saving"
          :disabled="saving"
        >
          {{ t("common.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { ref, watch } from "vue";
import type { VForm } from "vuetify/components";

const props = defineProps<{
  category: { id: string; initialAmount?: number } | null;
  saving?: boolean;
}>();
const emit = defineEmits<{ save: [value: number | null] }>();

const showDialog = defineModel<boolean>({ required: true });

const { t } = useI18n();
const { nonnegativeNumber } = useRules();
const form = ref<VForm>();

const initialAmount = ref<number | null>(null);

watch(
  () => [showDialog.value, props.category] as const,
  ([open, cat]) => {
    if (open) {
      initialAmount.value = cat?.initialAmount ?? null;
    }
  },
);

const save = async () => {
  const valid = await form.value?.validate();
  if (!valid?.valid) return;
  const value = initialAmount.value;
  emit("save", typeof value === "number" && Number.isFinite(value) ? value : null);
};
</script>
